import {
  formatExtrasSegmentDirectionLabel,
  getFlightSegmentsWithLegs,
  mapRawLdsSupplementsToMockupExtras,
} from "../../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";
import { resolveFlightBaggageDisplayState } from "../../Components/FlightBookingFlowMockup/adapters/flightBaggageStepPolicy";

function normalizeWeightLabel(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const m = s.match(/(\d+(?:\.\d+)?)\s*(KG|KGS|ק"?ג)?/i);
  if (!m) return s;
  return `${m[1]} ק"ג`;
}

function hebrewKind(type) {
  const t = String(type || "").trim().toUpperCase();
  if (t === "TROLLEY") return "טרולי";
  return "מזוודה";
}

function formatPriceLabel(unit, count, currency) {
  const n = Number(unit);
  if (!Number.isFinite(n) || n <= 0 || count <= 0) return "";
  const total = n * count;
  const cur = String(currency || "USD").trim().toUpperCase() || "USD";
  if (cur === "ILS" || cur === "NIS") {
    return `₪${Math.round(total).toLocaleString("he-IL")}`;
  }
  return `$${Math.round(total).toLocaleString("en-US")}`;
}

/**
 * Structured baggage rows for payment / order summary.
 * @returns {{ direction: string, title: string, paxCount: number, priceLabel: string }[]}
 */
export function buildUnifiedBaggageDetailItems({
  extras = null,
  rawDeal = null,
} = {}) {
  const selections = extras?.supplementPaxSelections;
  if (!selections || typeof selections !== "object" || !rawDeal) {
    return [];
  }

  const rows = mapRawLdsSupplementsToMockupExtras(rawDeal);
  const byKey = new Map(rows.map((r) => [r.supplementKey, r]));
  const numSeg = getFlightSegmentsWithLegs(rawDeal).length || 1;
  const items = [];

  for (const [key, ids] of Object.entries(selections)) {
    if (!Array.isArray(ids) || ids.length === 0) continue;
    const row = byKey.get(key);
    if (!row) continue;
    const paxCount = ids.length;
    const direction =
      formatExtrasSegmentDirectionLabel(row.segmentIndex, numSeg) || "";
    const weight = normalizeWeightLabel(row.weightLabel);
    const title = weight
      ? `${hebrewKind(row.type)} ${weight}`
      : hebrewKind(row.type);
    const unit = Number(extras?.supplementUnitFares?.[key] ?? row.fareAmount);
    items.push({
      direction,
      title,
      paxCount,
      priceLabel: formatPriceLabel(unit, paxCount, row.currency),
    });
  }

  return items;
}

/**
 * Human-readable baggage detail for payment / order summary.
 * Prefer selected paid extras; fall back to included-in-fare lines.
 */
export function buildUnifiedBaggageDetailLabel({
  extras = null,
  rawDeal = null,
  baggageLabel = "",
} = {}) {
  const items = buildUnifiedBaggageDetailItems({ extras, rawDeal });
  if (items.length) {
    return items
      .map((item) => {
        const parts = [
          item.direction || null,
          item.title,
          item.paxCount > 0 ? `${item.paxCount} נוסעים` : null,
          item.priceLabel || null,
        ].filter(Boolean);
        return parts.join(" · ");
      })
      .join("\n");
  }

  const state = rawDeal ? resolveFlightBaggageDisplayState(rawDeal) : null;
  const included = Array.isArray(state?.includedLines)
    ? state.includedLines.filter(Boolean)
    : [];
  if (included.length) return included.join("\n");

  const existing = String(baggageLabel || "").trim();
  if (existing && existing !== "כולל כבודה נוספת") return existing;
  return "";
}
